/**
 * DocChrome — back-compat alias for TopBar.
 *
 * The top chrome was originally exported as `DocChrome`. The canonical file is
 * now `TopBar.tsx`; this module re-exports it under both names with a small
 * prop alias (`unsaved` → `isDirty`, `onTitleChange` → `onTitle`,
 * `focusKeyword` → `focusKw`, `onFocusKeywordClick` → `onFocusKwClick`).
 */

import { TopBar, type TopBarProps, type BreadcrumbCrumb } from './TopBar';
import { SCORE_FOCUS_EVENT, type ScoreBreakdownEntry } from './ui';
import type { OverflowMenuProps } from './OverflowMenu';
import type { FormatCommand } from './FormatToolbar';

export type { BreadcrumbCrumb, FormatCommand };

export interface DocChromeProps
  extends Omit<TopBarProps, 'isDirty' | 'onTitle' | 'focusKw' | 'onFocusKwClick'> {
  /** Alias of TopBar.isDirty. */
  unsaved?: boolean;
  /** Alias of TopBar.onTitle. */
  onTitleChange: (value: string) => void;
  /** Alias of TopBar.focusKw. */
  focusKeyword?: string;
  /** Alias of TopBar.onFocusKwClick. */
  onFocusKeywordClick?: () => void;
}

export function DocChrome({
  unsaved,
  onTitleChange,
  focusKeyword,
  onFocusKeywordClick,
  onOpenQualityPanel,
  ...rest
}: DocChromeProps) {
  // Defensive default: if the integrator forgets to wire onOpenQualityPanel,
  // dispatch the standard ed-focus-quality event so the rail can react.
  // (ScoreBadge has the same fallback internally; this belt-and-braces it
  // because this is the user's #1 reported bug from chat1.md:903-905.)
  const handleOpenQualityPanel =
    onOpenQualityPanel ??
    (() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SCORE_FOCUS_EVENT));
      }
    });

  return (
    <TopBar
      {...rest}
      isDirty={unsaved}
      onTitle={onTitleChange}
      focusKw={focusKeyword}
      onFocusKwClick={onFocusKeywordClick}
      onOpenQualityPanel={handleOpenQualityPanel}
    />
  );
}

export type { ScoreBreakdownEntry, OverflowMenuProps };
